import { useState, useEffect, useCallback } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Bell, Bot, Users, Inbox, Code2, BarChart3, Send, Workflow,
  ArrowRight, Check, Zap, Shield, Globe, ChevronRight,
  MessageCircle, Smartphone, Activity, Lock,
  Twitter, Github, Linkedin, Mail
} from 'lucide-react';

/* ─── CSS Particle Background ─── */
function ParticleBg() {
  return (
    <div className="absolute inset-0 z-0 overflow-hidden">
      {Array.from({ length: 40 }).map((_, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-purple-500/20"
          style={{
            width: `${Math.random() * 3 + 1}px`,
            height: `${Math.random() * 3 + 1}px`,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animation: `float ${8 + Math.random() * 12}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 5}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); opacity: 0.3; }
          25% { transform: translateY(-20px) translateX(10px); opacity: 0.6; }
          50% { transform: translateY(-10px) translateX(-10px); opacity: 0.4; }
          75% { transform: translateY(-30px) translateX(5px); opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

/* ─── Animated Counter ─── */
function Counter({ end, suffix = '', duration = 2 }) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (!started) return;
    let start = 0;
    const step = end / (duration * 60);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [started, end, duration]);
  return (
    <motion.span
      onViewportEnter={() => setStarted(true)}
      viewport={{ once: true }}
    >
      {count.toLocaleString()}{suffix}
    </motion.span>
  );
}

/* ─── Section Wrapper ─── */
const Section = ({ children, className = '', id }) => (
  <section id={id} className={`relative py-24 sm:py-32 ${className}`}>
    <div className="max-w-7xl mx-auto px-6 lg:px-8">{children}</div>
  </section>
);

/* ─── Badge ─── */
const Badge = ({ children }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-purple-500/30 bg-purple-500/10 text-purple-300 text-xs font-mono uppercase tracking-[0.2em] mb-6"
  >
    {children}
  </motion.div>
);

/* ─── Fade-in Wrapper ─── */
const FadeIn = ({ children, delay = 0, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ========================================================================
   NAVBAR
   ======================================================================== */
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#030014]/80 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-purple-900/10'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/30 group-hover:shadow-purple-500/50 transition-shadow">
            <Zap size={18} className="text-white" />
          </div>
          <span className="text-lg font-bold text-white tracking-tight">
            Bot<span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-300">Wave</span>
          </span>
        </a>

        {/* Nav Links - hidden on mobile */}
        <div className="hidden md:flex items-center gap-8">
          {['Features', 'Pricing'].map((item) => (
            <a
              key={item}
              href={`#${item.toLowerCase()}`}
              className="text-sm text-slate-400 hover:text-white transition-colors duration-300"
            >
              {item}
            </a>
          ))}
        </div>

        {/* Auth Buttons */}
        <div className="flex items-center gap-3">
          <a
            href="https://botwave.in/login"
            data-testid="navbar-login-button"
            className="text-sm text-slate-300 hover:text-white transition-colors px-4 py-2"
          >
            Login
          </a>
          <a
            href="https://botwave.in/register"
            data-testid="navbar-signup-button"
            className="text-sm font-medium text-white px-5 py-2 rounded-full bg-gradient-to-r from-purple-600 to-violet-500 hover:from-purple-500 hover:to-violet-400 shadow-[0_0_20px_rgba(157,76,221,0.35)] hover:shadow-[0_0_30px_rgba(157,76,221,0.55)] transition-all duration-300"
          >
            Sign Up
          </a>
        </div>
      </div>
    </nav>
  );
}

/* ========================================================================
   HERO
   ======================================================================== */
function Hero() {
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 0.3], [0, -60]);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-16" data-testid="hero-section">
      {/* Gradient Orbs */}
      <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full bg-purple-600/15 blur-[160px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-violet-500/10 blur-[140px] pointer-events-none" />
      <ParticleBg />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10 grid lg:grid-cols-2 gap-16 items-center w-full">
        {/* Left - Text */}
        <div>
          <FadeIn>
            <Badge><Zap size={12} /> Next-Gen Platform</Badge>
          </FadeIn>

          <FadeIn delay={0.1}>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.05]">
              <span className="text-transparent bg-clip-text bg-gradient-to-b from-white via-white to-white/50">
                Next-Generation
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-violet-400 to-purple-300">
                WhatsApp Automation
              </span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-b from-white/80 to-white/40">
                Platform
              </span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.2}>
            <p className="mt-6 text-lg text-slate-400 leading-relaxed max-w-lg">
              Automate conversations, send alerts, manage chatbots, and scale customer engagement effortlessly.
            </p>
          </FadeIn>

          <FadeIn delay={0.3}>
            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="https://botwave.in/register"
                data-testid="hero-get-started-button"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-purple-600 to-violet-500 text-white font-semibold text-sm shadow-[0_0_30px_rgba(157,76,221,0.4)] hover:shadow-[0_0_50px_rgba(157,76,221,0.6)] transition-all duration-300"
              >
                Get Started
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#pricing"
                data-testid="hero-book-demo-button"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/15 bg-white/5 backdrop-blur-md text-white font-medium text-sm hover:bg-white/10 hover:border-white/25 transition-all duration-300"
              >
                Book Demo
              </a>
            </div>
          </FadeIn>

          {/* Mini Stats */}
          <FadeIn delay={0.4}>
            <div className="mt-12 flex gap-8 text-sm">
              {[
                { label: 'Active Users', value: '10K+' },
                { label: 'Messages/Day', value: '5M+' },
                { label: 'Uptime', value: '99.9%' },
              ].map((s) => (
                <div key={s.label}>
                  <div className="text-white font-semibold text-lg">{s.value}</div>
                  <div className="text-slate-500 text-xs mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
          </FadeIn>
        </div>

        {/* Right - Dashboard Mockup */}
        <FadeIn delay={0.2} className="hidden lg:block">
          <motion.div
            style={{ y }}
            className="relative"
          >
            {/* Glow behind */}
            <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 to-violet-600/20 rounded-3xl blur-2xl" />
            
            {/* Dashboard Card */}
            <div className="relative bg-[#0A0520]/90 border border-white/10 rounded-2xl p-6 backdrop-blur-xl shadow-2xl">
              {/* Top bar */}
              <div className="flex items-center gap-2 mb-6">
                <div className="w-3 h-3 rounded-full bg-red-500/70" />
                <div className="w-3 h-3 rounded-full bg-yellow-500/70" />
                <div className="w-3 h-3 rounded-full bg-green-500/70" />
                <div className="ml-4 flex-1 h-6 rounded-md bg-white/5" />
              </div>
              
              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                {[
                  { label: 'Messages Sent', val: '24,567', color: 'from-purple-500 to-violet-500' },
                  { label: 'Active Sessions', val: '142', color: 'from-emerald-500 to-teal-500' },
                  { label: 'Response Rate', val: '98.5%', color: 'from-blue-500 to-cyan-500' },
                ].map((s, i) => (
                  <motion.div
                    key={s.label}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="bg-white/5 rounded-xl p-4 border border-white/5"
                  >
                    <div className={`text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${s.color}`}>{s.val}</div>
                    <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                  </motion.div>
                ))}
              </div>

              {/* Chat Preview */}
              <div className="space-y-3">
                {[
                  { align: 'left', msg: 'Hi! I want to know about your services', time: '2:30 PM' },
                  { align: 'right', msg: 'Welcome! Here are our top packages...', time: '2:30 PM', bot: true },
                  { align: 'left', msg: 'Can I get a demo?', time: '2:31 PM' },
                ].map((m, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: m.align === 'left' ? -20 : 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.8 + i * 0.15 }}
                    className={`flex ${m.align === 'right' ? 'justify-end' : ''}`}
                  >
                    <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-xs ${
                      m.align === 'right'
                        ? 'bg-gradient-to-r from-purple-600/80 to-violet-600/80 text-white rounded-br-md'
                        : 'bg-white/10 text-slate-300 rounded-bl-md'
                    }`}>
                      {m.bot && <span className="text-purple-300 text-[10px] block mb-0.5">Bot Reply</span>}
                      {m.msg}
                      <span className="block text-[10px] mt-1 opacity-50 text-right">{m.time}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Floating Cards */}
            <motion.div
              animate={{ y: [0, -8, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
              className="absolute -top-6 -right-6 bg-[#0A0520]/90 border border-purple-500/30 rounded-xl p-3 backdrop-blur-xl shadow-lg shadow-purple-900/20"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                  <MessageCircle size={14} className="text-green-400" />
                </div>
                <div>
                  <div className="text-[10px] text-green-400 font-medium">Auto Reply</div>
                  <div className="text-[10px] text-slate-500">Sent in 0.3s</div>
                </div>
              </div>
            </motion.div>

            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
              className="absolute -bottom-4 -left-6 bg-[#0A0520]/90 border border-violet-500/30 rounded-xl p-3 backdrop-blur-xl shadow-lg shadow-violet-900/20"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Bot size={14} className="text-purple-400" />
                </div>
                <div>
                  <div className="text-[10px] text-purple-400 font-medium">AI Chatbot</div>
                  <div className="text-[10px] text-slate-500">Active</div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        </FadeIn>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#030014] to-transparent pointer-events-none" />
    </section>
  );
}

/* ========================================================================
   FEATURES
   ======================================================================== */
const FEATURES = [
  { icon: Bell, title: 'WhatsApp Alert Automation', desc: 'Trigger instant notifications for orders, payments, appointments, and custom events in real-time.', color: 'from-amber-500 to-orange-500' },
  { icon: Bot, title: 'AI Chatbots', desc: 'Deploy intelligent chatbots that handle FAQs, collect leads, and qualify prospects 24/7.', color: 'from-purple-500 to-violet-500' },
  { icon: Users, title: 'Multi-Agent System', desc: 'Connect multiple WhatsApp numbers across distributed workers with automatic load balancing.', color: 'from-blue-500 to-cyan-500' },
  { icon: Inbox, title: 'Shared Team Inbox', desc: 'Centralize all WhatsApp conversations for your entire team with smart routing and assignment.', color: 'from-emerald-500 to-teal-500' },
  { icon: Code2, title: 'API Integration', desc: 'RESTful APIs with webhook support. Integrate WhatsApp into any app, CRM, or workflow.', color: 'from-rose-500 to-pink-500' },
  { icon: BarChart3, title: 'Real-Time Analytics', desc: 'Track message delivery, engagement rates, bot performance, and customer insights live.', color: 'from-violet-500 to-purple-500' },
  { icon: Send, title: 'Bulk Messaging', desc: 'Send personalized messages to thousands with smart throttling and compliance safeguards.', color: 'from-sky-500 to-blue-500' },
  { icon: Workflow, title: 'Smart Workflow Automation', desc: 'Build complex multi-step automation flows with conditional logic and scheduled triggers.', color: 'from-fuchsia-500 to-pink-500' },
];

function Features() {
  return (
    <Section id="features">
      <div className="text-center mb-16">
        <Badge><Zap size={12} /> Capabilities</Badge>
        <FadeIn>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
            Everything you need to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-300">automate WhatsApp</span>
          </h2>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-4 text-slate-400 max-w-2xl mx-auto text-base sm:text-lg">
            A complete toolkit for modern WhatsApp automation - from simple alerts to complex multi-step flows.
          </p>
        </FadeIn>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {FEATURES.map((f, i) => (
          <FadeIn key={f.title} delay={i * 0.05}>
            <div
              data-testid={`feature-card-${i}`}
              className="group relative bg-white/[0.03] backdrop-blur-sm border border-white/[0.08] rounded-2xl p-6 hover:border-purple-500/40 hover:bg-white/[0.06] transition-all duration-500 h-full"
            >
              {/* Hover glow */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-b from-purple-600/5 to-transparent pointer-events-none" />

              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${f.color} bg-opacity-20 flex items-center justify-center mb-4 shadow-lg`}
                style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
                <f.icon size={20} className="text-white" />
              </div>
              <h3 className="text-white font-semibold text-sm mb-2">{f.title}</h3>
              <p className="text-slate-500 text-xs leading-relaxed">{f.desc}</p>
            </div>
          </FadeIn>
        ))}
      </div>
    </Section>
  );
}

/* ========================================================================
   SHOWCASE
   ======================================================================== */
function Showcase() {
  const items = [
    { icon: Activity, label: 'Real-Time Dashboard', value: 'Monitor everything live', color: 'from-purple-500 to-violet-500' },
    { icon: MessageCircle, label: 'Smart Conversations', value: 'AI-powered chat flows', color: 'from-emerald-500 to-teal-500' },
    { icon: Smartphone, label: 'Multi-Device', value: 'Connect unlimited numbers', color: 'from-blue-500 to-cyan-500' },
    { icon: Lock, label: 'Enterprise Security', value: 'End-to-end encryption', color: 'from-amber-500 to-orange-500' },
  ];

  return (
    <Section className="overflow-hidden">
      {/* Background Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-purple-600/10 blur-[120px] rounded-full" />
      </div>

      <div className="text-center mb-16 relative z-10">
        <Badge><Globe size={12} /> Platform</Badge>
        <FadeIn>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
            Built for{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-300">scale</span>
          </h2>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">
            Enterprise-grade infrastructure powering millions of messages across thousands of businesses.
          </p>
        </FadeIn>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 relative z-10">
        {items.map((item, i) => (
          <FadeIn key={item.label} delay={i * 0.1}>
            <div className="text-center p-8 rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-sm hover:border-purple-500/30 transition-all duration-500 group">
              <div className={`w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br ${item.color} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}
                style={{ backgroundColor: 'rgba(0,0,0,0.3)' }}>
                <item.icon size={24} className="text-white" />
              </div>
              <div className="text-white font-semibold text-sm mb-1">{item.label}</div>
              <div className="text-slate-500 text-xs">{item.value}</div>
            </div>
          </FadeIn>
        ))}
      </div>
    </Section>
  );
}

/* ========================================================================
   TRUST / STATS
   ======================================================================== */
function Trust() {
  const stats = [
    { value: 10000, suffix: '+', label: 'Active Users' },
    { value: 5, suffix: 'M+', label: 'Messages Sent Daily' },
    { value: 99, suffix: '.9%', label: 'Platform Uptime' },
    { value: 150, suffix: '+', label: 'Countries Served' },
  ];

  return (
    <Section>
      <div className="relative rounded-3xl border border-white/[0.08] bg-white/[0.02] p-12 sm:p-16 overflow-hidden">
        {/* Background Gradient */}
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-purple-600/10 blur-[100px] rounded-full pointer-events-none" />

        <div className="text-center mb-12 relative z-10">
          <Badge><Shield size={12} /> Trusted</Badge>
          <FadeIn>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-white">
              Trusted by businesses{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-300">worldwide</span>
            </h2>
          </FadeIn>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
          {stats.map((s, i) => (
            <FadeIn key={s.label} delay={i * 0.1}>
              <div className="text-center">
                <div className="text-4xl sm:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-b from-white to-white/60">
                  <Counter end={s.value} suffix={s.suffix} />
                </div>
                <div className="text-slate-500 text-sm mt-2">{s.label}</div>
              </div>
            </FadeIn>
          ))}
        </div>

        {/* Trust Badges */}
        <FadeIn delay={0.3}>
          <div className="flex flex-wrap justify-center gap-6 mt-12 pt-8 border-t border-white/5 relative z-10">
            {['Enterprise-Grade Infrastructure', 'SOC 2 Compliant', 'GDPR Ready', 'Secure WhatsApp API'].map((badge) => (
              <div key={badge} className="flex items-center gap-2 text-xs text-slate-500">
                <Check size={14} className="text-green-500" />
                {badge}
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </Section>
  );
}

/* ========================================================================
   PRICING
   ======================================================================== */
const PLANS = [
  {
    name: 'Starter',
    price: '999',
    period: '/mo',
    desc: 'Perfect for small businesses getting started with WhatsApp automation.',
    features: ['1 WhatsApp Number', '5,000 Messages/mo', 'Basic Chatbot', 'Email Support', 'API Access'],
    cta: 'Start Free Trial',
    highlight: false,
  },
  {
    name: 'Growth',
    price: '2,999',
    period: '/mo',
    desc: 'For growing teams that need advanced automation and multi-agent support.',
    features: ['5 WhatsApp Numbers', '50,000 Messages/mo', 'AI Chatbot + Flows', 'Priority Support', 'Advanced Analytics', 'Team Inbox', 'Webhook Integration'],
    cta: 'Get Started',
    highlight: true,
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    period: '',
    desc: 'Dedicated infrastructure and support for large-scale operations.',
    features: ['Unlimited Numbers', 'Unlimited Messages', 'Custom AI Models', 'Dedicated Support', 'SLA Guarantee', 'Custom Integrations', 'On-Premise Option', 'White Label'],
    cta: 'Contact Sales',
    highlight: false,
  },
];

function Pricing() {
  return (
    <Section id="pricing">
      <div className="text-center mb-16">
        <Badge><Zap size={12} /> Pricing</Badge>
        <FadeIn>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white">
            Simple,{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-300">transparent</span>
            {' '}pricing
          </h2>
        </FadeIn>
        <FadeIn delay={0.1}>
          <p className="mt-4 text-slate-400 max-w-xl mx-auto">
            Start free. Scale as you grow. No hidden fees.
          </p>
        </FadeIn>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
        {PLANS.map((plan, i) => (
          <FadeIn key={plan.name} delay={i * 0.1}>
            <div
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              className={`relative rounded-2xl p-8 h-full flex flex-col ${
                plan.highlight
                  ? 'bg-gradient-to-b from-purple-600/20 to-violet-600/5 border-2 border-purple-500/50 shadow-[0_0_40px_rgba(157,76,221,0.15)]'
                  : 'bg-white/[0.03] border border-white/[0.08]'
              }`}
            >
              {plan.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-purple-600 to-violet-500 text-white text-xs font-semibold">
                  Most Popular
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-white font-semibold text-lg">{plan.name}</h3>
                <div className="mt-3 flex items-baseline gap-1">
                  {plan.price !== 'Custom' && <span className="text-slate-500 text-sm">&#8377;</span>}
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-slate-500 text-sm">{plan.period}</span>
                </div>
                <p className="mt-3 text-slate-500 text-xs leading-relaxed">{plan.desc}</p>
              </div>

              <ul className="space-y-3 mb-8 flex-grow">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <Check size={14} className={plan.highlight ? 'text-purple-400' : 'text-slate-600'} />
                    {f}
                  </li>
                ))}
              </ul>

              <a
                href="https://botwave.in/register"
                className={`block text-center py-3 rounded-full text-sm font-semibold transition-all duration-300 ${
                  plan.highlight
                    ? 'bg-gradient-to-r from-purple-600 to-violet-500 text-white shadow-[0_0_20px_rgba(157,76,221,0.35)] hover:shadow-[0_0_30px_rgba(157,76,221,0.55)]'
                    : 'bg-white/5 border border-white/10 text-white hover:bg-white/10'
                }`}
              >
                {plan.cta}
              </a>
            </div>
          </FadeIn>
        ))}
      </div>
    </Section>
  );
}

/* ========================================================================
   CTA BANNER
   ======================================================================== */
function CTABanner() {
  return (
    <Section>
      <FadeIn>
        <div className="relative rounded-3xl overflow-hidden">
          {/* Glow */}
          <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 via-violet-600/20 to-purple-600/30 blur-xl" />
          
          <div className="relative bg-[#0A0520]/80 border border-purple-500/20 rounded-3xl p-12 sm:p-16 text-center backdrop-blur-xl">
            <h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">
              Ready to automate your{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-300">WhatsApp?</span>
            </h2>
            <p className="mt-4 text-slate-400 max-w-lg mx-auto">
              Join thousands of businesses using BotWave to engage customers at scale.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <a
                href="https://botwave.in/register"
                data-testid="cta-get-started-button"
                className="group inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-purple-600 to-violet-500 text-white font-semibold text-sm shadow-[0_0_30px_rgba(157,76,221,0.4)] hover:shadow-[0_0_50px_rgba(157,76,221,0.6)] transition-all duration-300"
              >
                Get Started Free
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>
        </div>
      </FadeIn>
    </Section>
  );
}

/* ========================================================================
   FOOTER
   ======================================================================== */
function Footer() {
  const links = {
    Product: [
      { label: 'Features', href: '#features' },
      { label: 'Pricing', href: '#pricing' },
      { label: 'API Docs', href: '#' },
      { label: 'Integrations', href: '#' },
    ],
    Company: [
      { label: 'About', href: '#' },
      { label: 'Blog', href: '#' },
      { label: 'Careers', href: '#' },
      { label: 'Contact', href: '#' },
    ],
    Legal: [
      { label: 'Privacy Policy', href: '#' },
      { label: 'Terms of Service', href: '#' },
      { label: 'Cookie Policy', href: '#' },
    ],
  };

  return (
    <footer className="relative border-t border-white/[0.06] bg-[#020010]" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-5 gap-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <a href="/" className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center shadow-lg shadow-purple-500/30">
                <Zap size={18} className="text-white" />
              </div>
              <span className="text-lg font-bold text-white tracking-tight">
                Bot<span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-violet-300">Wave</span>
              </span>
            </a>
            <p className="mt-4 text-slate-500 text-sm leading-relaxed max-w-xs">
              Next-generation WhatsApp automation platform for modern businesses.
            </p>
            <div className="flex gap-3 mt-6">
              {[Twitter, Github, Linkedin, Mail].map((Icon, i) => (
                <a
                  key={i}
                  href="#"
                  className="w-9 h-9 rounded-lg bg-white/5 border border-white/[0.08] flex items-center justify-center text-slate-500 hover:text-white hover:border-purple-500/40 transition-all duration-300"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {/* Links */}
          {Object.entries(links).map(([title, items]) => (
            <div key={title}>
              <h4 className="text-white font-semibold text-sm mb-4">{title}</h4>
              <ul className="space-y-2.5">
                {items.map((link) => (
                  <li key={link.label}>
                    <a href={link.href} className="text-sm text-slate-500 hover:text-slate-300 transition-colors">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-white/[0.06] flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="text-xs text-slate-600">
            &copy; {new Date().getFullYear()} BotWave. All rights reserved.
          </div>
          <div className="text-xs text-slate-600">
            Made with passion in India
          </div>
        </div>
      </div>
    </footer>
  );
}

/* ========================================================================
   MAIN LANDING PAGE
   ======================================================================== */
export default function LandingPage() {
  useEffect(() => {
    // Load Inter + Outfit fonts only for landing page
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Outfit:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  return (
    <div className="bg-[#030014] text-white min-h-screen antialiased overflow-x-hidden" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <Navbar />
      <Hero />
      <Features />
      <Showcase />
      <Trust />
      <Pricing />
      <CTABanner />
      <Footer />
    </div>
  );
}
